import React, {useEffect, useState} from 'react'
import {useLocation, useNavigate} from 'react-router-dom'
import {SettingPageTitle} from '../components/header/SettingPageTitle'
import {AddAuditor} from '../components/drawer/AddAuditor'
import {collection, onSnapshot, doc, deleteDoc} from '@firebase/firestore'
import {useAuth} from 'app/modules/auth'
import db from 'services/firebase'
import {KTIcon} from '_metronic/helpers'
import {showErrorToast, showSuccessToast} from 'app/components/ShowToast'
import ConfirmationModal from 'app/components/ConfirmationModal'
import {ToastContainer} from 'react-toastify'
import Searchbar from 'app/components/Searchbar'
import {getFunctions, httpsCallable} from 'firebase/functions'
import NavTabs from 'app/components/NavTabs'
import { ListLoading } from 'app/components/ListLoading'
import EmptyState from 'app/components/EmptyState'

const tabs = [
  {
    title: 'Your Organization',
    slug: 'users',
    href: '/company-settings/users',
  },
  {
    title: 'Auditors',
    slug: 'auditors',
    href: '/company-settings/users/auditors',
  },
  // {
  //   title: 'Managed Service Providers',
  //   slug: 'msp',
  //   href: '/company-settings/msp',
  // },
]

const UserAuditors = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState(
    location.pathname.split('/').at(-1) === ''
      ? tabs[0].slug
      : location.pathname.split('/').at(-1)
  )
  const [isOpen, setIsOpen] = useState(false)
  const [users, setUsers] = useState<any[]>([])
  const {currentUser} = useAuth()
  const [searchTerm, setSearchTerm] = useState('')
  const [activeFrameworks, setActiveFrameworks] = useState<string[]>([])
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [selectedAuditor, setSelectedAuditor] = useState<any>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const functions = getFunctions()
  const reinviteEmployeeFunction = httpsCallable(functions, 'reinviteEmployee')
  const [isDropdownVisible, setDropdownVisible] = useState(false)
  const [isDataFetching, setIsDataFetching] = useState(true)

  useEffect(() => {
    if (location.pathname.includes('auditors')) {
      setActiveTab('auditors')
    } else if (location.pathname.includes('msp')) {
      setActiveTab('msp')
    } else {
      setActiveTab('users')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    getPersonnel()
  }, [])

  const getPersonnel = () => {
    try {
      setIsDataFetching(true)
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      const PolicyRef = collection(tenantRef, 'employees')
      const unsubscribe = onSnapshot(PolicyRef, (snapshot) => {
        const risksData = snapshot.docs
          .map((doc) => ({
            ...doc.data(),
            id: doc.id,
            role: doc.data().role,
          }))
          .filter((item) => item.role === 'Auditor')

        setUsers(risksData)
      })

      return unsubscribe
    } catch (error) {
      console.error('Error getting personnels:', error)
    } finally {
      setTimeout(() => setIsDataFetching(false), 500)
    }
  }

  useEffect(() => {
    getActiveFrameworks()
  }, [])

  const getActiveFrameworks = () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      const testsCollectionRef = collection(tenantRef, 'active_frameworks')

      const unsubscribe = onSnapshot(testsCollectionRef, (snapshot) => {
        const frameworksData = snapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
          frameworks: doc.data().frameworks,
        }))
        const frameworks = frameworksData[0]?.frameworks || []
        setActiveFrameworks(frameworks)
      })

      return unsubscribe
    } catch (error) {
      console.error('Error getting frameworks:', error)
    }
  }

  const toggleDrawer = () => {
    setIsOpen(!isOpen)
  }

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    if (params.get('open') === 'true') {
      setIsOpen(true)
    }
  }, [location.search])

  const closeDrawer = () => {
    setIsOpen(false)
    const params = new URLSearchParams(location.search)
    if (params.get('open') === 'true') {
      params.delete('open')
      navigate({pathname: location.pathname, search: params.toString()}, {replace: true})
    }
  }

  const filteredUsers = users.filter((user) => user.full_name.toLowerCase().includes(searchTerm))

  const handleDelete = (item) => {
    setSelectedAuditor(item)
    setIsDeleteModalOpen(true)
  }

  const handleConfirmDelete = async () => {
    try {
      setIsDeleting(true)
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      const employeeRef = collection(tenantRef, 'employees')
      const docRef = doc(employeeRef, selectedAuditor.id)
      await deleteDoc(docRef)
      if(selectedAuditor.uid){
        const userRef = collection(db, 'users')
        const docsRef = doc(userRef, selectedAuditor.uid)
        await deleteDoc(docsRef)
      }
      showSuccessToast({
        message: `${selectedAuditor.full_name} has been removed from the organization successfully!`,
      })
    } catch (error) {
      showErrorToast({message: 'An error occurred while removing an employee.'})
    } finally {
      setIsDeleting(false)
      setIsDeleteModalOpen(false)
      setSelectedAuditor(null)
    }
  }

  const handleReinvite = async (user: any) => {
    try {
      const result = await reinviteEmployeeFunction({
        tenantId: currentUser?.tenantId,
        employeeId: user.id,
      })
      const data = result.data as {success: boolean}
      if (data.success) {
        showSuccessToast({message: 'Reinvite sent successfully!'})
      }
    } catch (error) {
      showErrorToast({message: 'An error occurred while sending a re-invite email.'})
    }
  }

  const toggleDropdown = () => {
    setDropdownVisible(!isDropdownVisible)
  }

  return (
    <>
      <SettingPageTitle
        icon='Settings.svg'
        title='Settings'
        desc=' Manage your settings'
        // breadcrumbs={usersBreadcrumbs}
      >
        Personnel Management
      </SettingPageTitle>
      <AddAuditor isOpen={isOpen} onClose={closeDrawer} />
      <div className={`card mb-5 mb-xl-8  `}>
        <div className='mt-4'>
          <NavTabs tabs={tabs} activeTab={activeTab!} />
        </div>

        <div className='card-body'>
          <div className='d-flex align-items-center justify-content-between mb-4'>
            <div className='flex-lg-fill'>
              <Searchbar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
            </div>

            <div>
              <button
                type='button'
                className='btn btn-sm fs-6'
                onClick={toggleDrawer}
                style={{height: '40px', background: '#0E9F6E', color: '#fff'}}
                onMouseEnter={(e: React.MouseEvent<HTMLElement>) =>
                  (e.currentTarget.style.backgroundColor = '#148363')
                }
                onMouseLeave={(e: React.MouseEvent<HTMLElement>) =>
                  (e.currentTarget.style.backgroundColor = '#0E9F6E')
                }
                disabled={activeFrameworks.length <= 0}
              >
                <KTIcon iconName='plus' className='fs-2 text-white' />
                Add Auditor
              </button>
            </div>
          </div>

          {isDataFetching ? (
            <div className='position-relative' style={{height:'245px'}}>
              <ListLoading />
            </div>
          ) : filteredUsers.length > 0 ? (
            <div className='table-responsive' style={{height: '500px', overflowY: 'auto'}}>
              <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                <thead>
                  <tr className='fw-bold fs-7' style={{color: '#5A5A5A'}}>
                    <th className='min-w-200px '>NAME</th>
                    <th className='min-w-200px'>EMAIL</th>
                    <th className='min-w-200px '>ORGANIZATION</th>
                    <th className='min-w-150px '>ROLE</th>
                    <th className='min-w-150px '>AVAILABILITY</th>
                    <th className='min-w-0px '></th>
                  </tr>
                </thead>

                <tbody>
                  {filteredUsers.map((user) => (
                    <tr key={user.id}>
                      <td>
                        <div className='d-flex align-items-center fs-6 fw-semibold'>
                          {user.full_name}
                        </div>
                      </td>
                      <td>
                        <div className='d-flex align-items-center fw-semibold'>{user.email}</div>
                      </td>
                      <td>
                        <div className='d-flex align-items-center fw-semibold'>{user.organization}</div>
                      </td>
                      <td>
                        <div className='d-flex align-items-center fw-semibold'>{user.role}</div>
                      </td>

                      <td>
                        <div className='d-flex align-items-center fw-bold'>
                          {user?.uid ? (
                            <span
                              className='badge fs-7'
                              style={{
                                color: user.availability ? '#0E9F6E' : '#FFA500',
                                background: user.availability ? '#E8F8F4' : '#FFF5E5',
                              }}
                            >
                              {user.availability ? 'Open' : 'Engaged'}
                            </span>
                          ) : (
                            <span
                              className='badge fs-7'
                              style={{
                                color: '#3B82F6',
                                background: '#E3F0FF',
                              }}
                            >
                              Invited
                            </span>
                          )}
                        </div>
                      </td>
                      <td className='text-end'>
                        <div className='dropdown mx-2'>
                          <button
                            className='btn btn-secondary border-0 p-0 m-2'
                            type='button'
                            id='dropdownMenu2'
                            data-bs-toggle='dropdown'
                            aria-haspopup='true'
                            aria-expanded='false'
                          >
                            <img
                              src='/media/buttons/Options.svg'
                              alt='Options'
                              onClick={toggleDropdown}
                            />
                          </button>
                          <ul className='dropdown-menu' aria-labelledby='dropdownMenu2'>
                          {!user?.uid && (
                            <>
                              <li>
                                <button
                                  className='dropdown-item text-hover-primary'
                                  type='button'
                                  style={{color: '#262626'}}
                                  onClick={(e) => handleReinvite(user)}
                                >
                                  <svg
                                    xmlns='http://www.w3.org/2000/svg'
                                    width='18'
                                    height='18'
                                    viewBox='0 0 24 24'
                                    fill='none'
                                    stroke='currentColor'
                                    stroke-width='2'
                                    stroke-linecap='round'
                                    stroke-linejoin='round'
                                    className='icon icon-tabler icons-tabler-outline icon-tabler-send mx-1 mb-1'
                                  >
                                    <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                                    <path d='M10 14l11 -11' />
                                    <path d='M21 3l-6.5 18a.55 .55 0 0 1 -1 0l-3.5 -7l-7 -3.5a.55 .55 0 0 1 0 -1l18 -6.5' />
                                  </svg>{' '}
                                  Resend Invite
                                </button>
                              </li>
                              <div className='separator my-1'></div>
                               </>
                            )}
                            {currentUser?.role === 'Owner' ? (
                              <>
                                <li>
                                  <button
                                    className='dropdown-item text-danger'
                                    type='button'
                                    style={{color: '#F15363'}}
                                    onClick={(e) => handleDelete(user)}
                                  >
                                    <svg
                                      xmlns='http://www.w3.org/2000/svg'
                                      width='18'
                                      height='18'
                                      viewBox='0 0 24 24'
                                      fill='none'
                                      stroke='currentColor'
                                      stroke-width='2'
                                      stroke-linecap='round'
                                      stroke-linejoin='round'
                                      className='icon icon-tabler icons-tabler-outline icon-tabler-trash mx-1 mb-1'
                                    >
                                      <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                                      <path d='M4 7l16 0' />
                                      <path d='M10 11l0 6' />
                                      <path d='M14 11l0 6' />
                                      <path d='M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12' />
                                      <path d='M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3' />
                                    </svg>{' '}
                                    Remove Auditor
                                  </button>
                                </li>
                              </>
                            ) : null}
                          </ul>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState
              title={users.length === 0 && searchTerm === '' ? 'No Auditor Found' : 'No Results Found'}
              description={users.length === 0 && searchTerm === ''
                ? `Auditors can review your controls and evidence, but cannot access sensitive employee data or change user access.`
                : 'No auditors match your search.'}
            />
          )}
        </div>
      </div>
      {isDeleteModalOpen && (
        <ConfirmationModal
          isModalOpen={isDeleteModalOpen}
          hideModal={() => setIsDeleteModalOpen(false)}
          confirmBtnClickHandler={handleConfirmDelete}
          toDeleteItemTitle={selectedAuditor?.full_name}
          isLoading={isDeleting}
          title={`Remove ${selectedAuditor?.full_name}`}
          message={`Are you sure you want to remove auditor ${selectedAuditor?.full_name} from the organization? This action is permanent and cannot be undone.`}
          type='delete'
          confirmButtonText='Remove'
        />
      )}
      <ToastContainer />
    </>
  )
}

export default UserAuditors
